import React, { useState, useEffect } from 'react';
import {
  Typography,
  TextField,
  Button,
  Grid,
  DialogContent,
  DialogActions,
  DialogTitle,
  Box,
  Checkbox,
  FormControlLabel,
  Dialog,
  CircularProgress,
} from '@mui/material';
import ScanningDialog from './components/ScanningDialog';

const ConfirmationPage = ({ docType, file, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [openScanningDialog, setOpenScanningDialog] = useState(true);

  const handleSubmit = async (formData) => {
    setLoading(true);
    setOpenScanningDialog(false);
    try {
      const submitData = {
        ...formData,
        applicationType: docType,
        applicationFile: file
      };
      
      // 模拟API调用
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      alert('Insurance confirmation generated successfully!');
      onClose();
    } catch (error) {
      console.error('Failed to generate confirmation:', error);
      alert('Failed to generate confirmation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const [formData, setFormData] = useState({
    insured: '',
    address: '',
    vehicle: '',
    vin: '',
    effectiveDate: '',
    expiryDate: '',
    insurer: '',
    hasLessor: false,
    hasFinance: false,
    lessorOrLender: '',
    policyNo: '',
    thirdPartyLiability: '',
    collisionDeductible: '',
    comprehensiveDeductible: '',
  });

  const handleChange = (event) => {
    const { name, value, checked } = event.target;
    if (name === 'hasLessor') {
      setFormData({
        ...formData,
        hasLessor: checked,
        hasFinance: false,
        lessor: checked ? formData.lessor : '',
      });
    } else if (name === 'hasFinance') {
      setFormData({
        ...formData,
        hasFinance: checked,
        hasLessor: false,
        lender: checked ? formData.lender : '',
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const formatDate = (date) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const d = new Date(date);
    return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  };

  // 添加一个通用的 inputProps 配置
  const commonInputProps = {
    'aria-label': 'Required field',  // 为了可访问性
    'title': 'Required field',       // 英文提示
    'lang': 'en'                     // 强制使用英文
  };

  return (
    <>
      <ScanningDialog 
        open={openScanningDialog}
        onComplete={() => setOpenScanningDialog(false)}
      />

      {/* Loading Dialog */}
      <Dialog 
        open={loading}
        PaperProps={{
          sx: {
            borderRadius: 1,
            minWidth: '300px'
          }
        }}
      >
        <DialogContent sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          py: 4
        }}>
          <CircularProgress size={24} />
          <Typography>Generating Document...</Typography>
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <DialogTitle>
        {docType === 'auto_insurance_confirmation' 
          ? 'Auto Insurance Confirmation'
          : 'Property Insurance Confirmation'
        }
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2}>
            {/* Basic Information Section */}
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                my: 1,
                '&::before, &::after': {
                  content: '""',
                  flex: 1,
                  borderBottom: '1px solid #e0e0e0'
                }
              }}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    px: 2,
                    color: '#1976d2',
                    fontWeight: 500,
                    textAlign: 'center'
                  }}
                >
                  Basic Information
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                name="insured"
                label="Insured"
                value={formData.insured}
                onChange={handleChange}
                variant="outlined"
                size="small"
                inputProps={commonInputProps}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                name="address"
                label="Address"
                value={formData.address}
                onChange={handleChange}
                variant="outlined"
                size="small"
                inputProps={commonInputProps}
              />
            </Grid>

            {/* Vehicle Information Section */}
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                my: 1,
                '&::before, &::after': {
                  content: '""',
                  flex: 1,
                  borderBottom: '1px solid #e0e0e0'
                }
              }}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ px: 2, color: '#1976d2', fontWeight: 500 }}
                >
                  Vehicle Information
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                name="vehicle"
                label="Vehicle"
                value={formData.vehicle}
                onChange={handleChange}
                variant="outlined"
                size="small"
                inputProps={commonInputProps}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                name="vin"
                label="VIN"
                value={formData.vin}
                onChange={handleChange}
                variant="outlined"
                size="small"
                inputProps={commonInputProps}
              />
            </Grid>

            {/* Policy Information Section */}
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                my: 1,
                '&::before, &::after': {
                  content: '""',
                  flex: 1,
                  borderBottom: '1px solid #e0e0e0'
                }
              }}>
                <Typography variant="subtitle2" sx={{ px: 2, color: '#1976d2', fontWeight: 500 }}>
                  Policy Information
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                required
                fullWidth
                name="effectiveDate"
                label="Effective Date"
                value={formData.effectiveDate}
                onChange={handleChange}
                variant="outlined"
                size="small"
                inputProps={commonInputProps}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                required
                fullWidth
                name="expiryDate"
                label="Expiry Date"
                value={formData.expiryDate}
                onChange={handleChange}
                variant="outlined"
                size="small"
                inputProps={commonInputProps}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                required
                fullWidth
                name="insurer"
                label="Insurer"
                value={formData.insurer}
                onChange={handleChange}
                variant="outlined"
                size="small"
                inputProps={commonInputProps}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                required
                fullWidth
                name="policyNo"
                label="Policy No."
                value={formData.policyNo}
                onChange={handleChange}
                variant="outlined"
                size="small"
                inputProps={commonInputProps}
              />
            </Grid>

            {/* 修改机构名称字段标签为固定文本，使用正确的术语 */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="lessorOrLender"
                label="Lessor or Lender"
                value={formData.lessorOrLender}
                onChange={handleChange}
                variant="outlined"
                size="small"
                inputProps={{
                  'aria-label': 'Optional field',
                  'title': 'Optional field',
                  'lang': 'en'
                }}
              />
            </Grid>

            {/* Coverage Section */}
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                my: 1,
                '&::before, &::after': {
                  content: '""',
                  flex: 1,
                  borderBottom: '1px solid #e0e0e0'
                }
              }}>
                <Typography variant="subtitle2" sx={{ px: 2, color: '#1976d2', fontWeight: 500 }}>
                  Coverage
                </Typography>
              </Box>
            </Grid>

            {/* Endorsement Selection */}
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex',
                gap: 4,
                justifyContent: 'center',
                mb: 3
              }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.hasLessor}
                      onChange={(e) => handleChange({ 
                        target: { 
                          name: 'hasLessor', 
                          checked: e.target.checked 
                        }
                      })}
                      sx={{
                        color: '#1976d2',
                        '&.Mui-checked': {
                          color: '#1976d2',
                        },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          fontWeight: 400,
                        }}
                      >
                        OPCF 5A
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          ml: 1,
                          color: '#666',
                          fontWeight: 400
                        }}
                      >
                        (Leased Vehicle)
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.hasFinance}
                      onChange={(e) => handleChange({ 
                        target: { 
                          name: 'hasFinance', 
                          checked: e.target.checked 
                        }
                      })}
                      sx={{
                        color: '#1976d2',
                        '&.Mui-checked': {
                          color: '#1976d2',
                        },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          fontWeight: 400,
                        }}
                      >
                        OPCF 23A
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          ml: 1,
                          color: '#666',
                          fontWeight: 400
                        }}
                      >
                        (Financed Vehicle)
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </Grid>

            {/* Coverage Fields */}
            <Grid item xs={12} md={4}>
              <TextField
                required
                fullWidth
                name="thirdPartyLiability"
                label="Third Party Liability"
                type="number"
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                }}
                inputProps={commonInputProps}
                value={formData.thirdPartyLiability}
                onChange={handleChange}
                variant="outlined"
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                required
                fullWidth
                name="collisionDeductible"
                label="Collision Deductible"
                type="number"
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                }}
                inputProps={commonInputProps}
                value={formData.collisionDeductible}
                onChange={handleChange}
                variant="outlined"
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                required
                fullWidth
                name="comprehensiveDeductible"
                label="Comprehensive Deductible"
                type="number"
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                }}
                inputProps={commonInputProps}
                value={formData.comprehensiveDeductible}
                onChange={handleChange}
                variant="outlined"
                size="small"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions 
        sx={{ 
          p: 2,
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa'
        }}
      >
        <Button variant="outlined" onClick={onClose} sx={{ mr: 1 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => handleSubmit(formData)}
          sx={{
            backgroundColor: '#1976d2',
            '&:hover': {
              backgroundColor: '#1565c0',
            },
          }}
        >
          Generate Confirmation
        </Button>
      </DialogActions>
    </>
  );
};

export default ConfirmationPage; 